import React, { useState, useEffect, useRef } from 'react';
import './AgentDashboard.css';
import './Sidebar.css';
import './PaidNoCertAttempt.css';
import { getApiBaseUrl, getApiUrl } from '../config/api';
import CoverPurchase from './CoverPurchase';
import EmployeeSidebar from './EmployeeSidebar';

const EmployeeDashboard = ({ user, onLogout, onBackToMain }) => {
  const [covers, setCovers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCoverPurchase, setShowCoverPurchase] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [purchaseEntryStep, setPurchaseEntryStep] = useState('product-selection');
  const [showCertificateRecovery, setShowCertificateRecovery] = useState(false);
  const [certRecoveryItems, setCertRecoveryItems] = useState([]);
  const [certRecoveryLoading, setCertRecoveryLoading] = useState(false);
  const [certRecoveryError, setCertRecoveryError] = useState('');
  const [certRecoverySearch, setCertRecoverySearch] = useState('');
  const [certRecoveryStatusScope, setCertRecoveryStatusScope] = useState('pending');
  const [certRecoveryPagination, setCertRecoveryPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [certRetryingClientId, setCertRetryingClientId] = useState(null);
  const [certRetryMessage, setCertRetryMessage] = useState(null);
  const [certRetryDateModal, setCertRetryDateModal] = useState(null);
  const [certRetryModalStart, setCertRetryModalStart] = useState('');
  const [certRetryModalSuggested, setCertRetryModalSuggested] = useState(null);
  const [certRetryModalSuggestedDb, setCertRetryModalSuggestedDb] = useState(null);
  const [certRetryModalSuggestedDmvic, setCertRetryModalSuggestedDmvic] = useState(null);
  const [certRetryModalOtherEnd, setCertRetryModalOtherEnd] = useState(null);
  const [certRetryModalLoadingSuggested, setCertRetryModalLoadingSuggested] = useState(false);
  const certPollingIntervalRef = useRef(null);
  const showCertificateRecoveryRef = useRef(false);
  const certRecoveryPageRef = useRef(1);

  useEffect(() => {
    fetchCovers(1);
  }, []);

  const fetchCovers = async (page = 1) => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('employeeToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });

      const response = await fetch(`${getApiBaseUrl()}/api/employee/covers?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('employeeToken');
          localStorage.removeItem('employeeUser');
          localStorage.removeItem('isEmployee');
          onLogout();
          return;
        }
        throw new Error('Failed to fetch covers');
      }

      const data = await response.json();

      if (data.success) {
        setCovers(data.covers || []);
        setPagination(data.pagination || pagination);
      } else {
        throw new Error(data.error || 'Failed to fetch covers');
      }
    } catch (err) {
      console.error('Error fetching covers:', err);
      setError(err.message || 'Failed to load covers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    showCertificateRecoveryRef.current = showCertificateRecovery;
  }, [showCertificateRecovery]);

  const fetchCertificateRecovery = async (page = 1) => {
    setCertRecoveryLoading(true);
    setCertRecoveryError('');
    certRecoveryPageRef.current = page;
    try {
      const token = localStorage.getItem('employeeToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: certRecoveryPagination.limit.toString(),
        statusScope: certRecoveryStatusScope,
        ...(certRecoverySearch && String(certRecoverySearch).trim() && { search: certRecoverySearch.trim() })
      });
      const response = await fetch(getApiUrl('/api/employee/certificate-recovery') + '?' + params, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('employeeToken');
          localStorage.removeItem('employeeUser');
          localStorage.removeItem('isEmployee');
          onLogout();
          return;
        }
        throw new Error('Failed to fetch certificate recovery list');
      }
      const data = await response.json();
      if (data.success) {
        setCertRecoveryItems(data.data.items || []);
        setCertRecoveryPagination(data.data.pagination || certRecoveryPagination);
      } else {
        throw new Error(data.error || 'Failed to load list');
      }
    } catch (err) {
      console.error('Certificate recovery fetch:', err);
      setCertRecoveryError(err.message || 'Failed to load certificate recovery list');
    } finally {
      setCertRecoveryLoading(false);
    }
  };

  useEffect(() => {
    if (showCertificateRecovery) {
      fetchCertificateRecovery(1);
    }
  }, [showCertificateRecovery]);

  useEffect(() => {
    if (showCertificateRecovery) {
      fetchCertificateRecovery(1);
    }
  }, [certRecoveryStatusScope]);

  const stopCertPolling = () => {
    if (certPollingIntervalRef.current) {
      clearInterval(certPollingIntervalRef.current);
      certPollingIntervalRef.current = null;
    }
  };

  useEffect(() => () => stopCertPolling(), []);

  const refreshListsAfterCertEvent = () => {
    fetchCovers(1);
    if (showCertificateRecoveryRef.current) {
      fetchCertificateRecovery(certRecoveryPageRef.current);
    }
  };

  const retryCertificateForClient = async (clientId, rectification = null, onQueued = null) => {
    setCertRetryMessage(null);
    stopCertPolling();
    setCertRetryingClientId(clientId);
    try {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        setCertRetryMessage('Please log in again.');
        return;
      }
      const response = await fetch(getApiUrl('/api/employee/certificate-retry'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId, ...(rectification || {}) })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setCertRetryMessage(data.error || 'Failed to queue retry.');
        return;
      }
      if (typeof onQueued === 'function') {
        try {
          onQueued();
        } catch (_) { /* ignore */ }
      }
      const jobId = data.jobId;
      if (!jobId && data.success && data.certificateNumber) {
        setCertRetryingClientId(null);
        setCertRetryMessage(
          data.alreadyIssued
            ? (data.message || `Certificate ${data.certificateNumber} is already active.`)
            : `Certificate issued: ${data.certificateNumber}`
        );
        refreshListsAfterCertEvent();
        setTimeout(() => {
          setCertRetryMessage(null);
        }, 5000);
        return;
      }
      setCertRetryMessage('Certificate queued for retry. Checking status…');
      refreshListsAfterCertEvent();
      if (!jobId) {
        setCertRetryingClientId(null);
        setTimeout(() => {
          setCertRetryMessage(null);
        }, 4000);
        return;
      }
      const pollMs = 1000;
      const maxAttempts = 90;
      let attempts = 0;
      certPollingIntervalRef.current = setInterval(async () => {
        attempts++;
        if (attempts > maxAttempts) {
          stopCertPolling();
          setCertRetryingClientId(null);
          setCertRetryMessage('Status check timed out. Refresh the list to see the latest.');
          refreshListsAfterCertEvent();
          setTimeout(() => setCertRetryMessage(null), 6000);
          return;
        }
        try {
          const statusRes = await fetch(
            getApiUrl(`/api/certificate-status/${jobId}?clientId=${encodeURIComponent(clientId)}`)
          );
          const statusData = await statusRes.json().catch(() => ({}));
          const state = statusData.state || statusData.status;
          if (statusData.downloadReady || statusData.certificateNumber || state === 'completed' || state === 'issued') {
            stopCertPolling();
            setCertRetryingClientId(null);
            setCertRetryMessage(
              statusData.certificateNumber
                ? `Certificate ${statusData.certificateNumber} is ready.`
                : 'Certificate issued successfully. List updated.'
            );
            setTimeout(() => setCertRetryMessage(null), 5000);
            refreshListsAfterCertEvent();
            return;
          }
          if (state === 'failed' && !statusData.downloadReady && !statusData.certificateNumber) {
            stopCertPolling();
            setCertRetryingClientId(null);
            setCertRetryMessage(statusData.error || 'Retry failed. Try again or contact support.');
            setTimeout(() => setCertRetryMessage(null), 6000);
            refreshListsAfterCertEvent();
            return;
          }
        } catch (_) {
          // keep polling
        }
      }, pollMs);
    } catch (err) {
      setCertRetryMessage(err.message || 'Failed to queue retry.');
      setTimeout(() => {
        setCertRetryMessage(null);
      }, 5000);
      setCertRetryingClientId(null);
    }
  };

  const openFixAndRetry = (row) => {
    if (!row?.client_id) return;
    const currentEmail = row.email_address || '';
    const currentPhone = row.phone_number || '';
    const emailInput = window.prompt('Enter corrected email address (leave blank to keep current):', currentEmail);
    if (emailInput === null) return;
    const phoneInput = window.prompt('Enter corrected phone number (07..., 01..., 7..., 1..., or 2547/2541...; leave blank to keep current):', currentPhone);
    if (phoneInput === null) return;
    const payload = {};
    if (String(emailInput).trim()) payload.emailAddress = String(emailInput).trim();
    if (String(phoneInput).trim()) payload.phoneNumber = String(phoneInput).trim();
    retryCertificateForClient(row.client_id, payload);
  };

  const correctPlateNtsaMismatchAndRecover = async (row) => {
    if (!row?.client_id) return;
    const def = (row.registration_number || '').trim();
    const plate = window.prompt('Enter the correct full registration as on NTSA (e.g. KAN269U):', def);
    if (plate == null) return;
    const trimmed = String(plate).trim();
    if (!trimmed) {
      setCertRetryMessage('Enter a registration number.');
      setTimeout(() => setCertRetryMessage(null), 4000);
      return;
    }
    setCertRetryMessage(null);
    setCertRetryingClientId(row.client_id);
    try {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        setCertRetryMessage('Please log in again.');
        setCertRetryingClientId(null);
        return;
      }
      const res = await fetch(getApiUrl('/api/employee/correct-plate-ntsa-mismatch'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: row.client_id, correctRegistrationNumber: trimmed })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setCertRetryMessage(data.error || 'Could not update plate from NTSA.');
        setCertRetryingClientId(null);
        setTimeout(() => setCertRetryMessage(null), 7000);
        refreshListsAfterCertEvent();
        return;
      }
      await retryCertificateForClient(row.client_id);
    } catch (e) {
      setCertRetryMessage(e.message || 'Request failed');
      setCertRetryingClientId(null);
      setTimeout(() => setCertRetryMessage(null), 6000);
      refreshListsAfterCertEvent();
    }
  };

  const closeCertRetryDateModal = () => {
    setCertRetryDateModal(null);
    setCertRetryModalStart('');
    setCertRetryModalSuggested(null);
    setCertRetryModalSuggestedDb(null);
    setCertRetryModalSuggestedDmvic(null);
    setCertRetryModalOtherEnd(null);
    setCertRetryModalLoadingSuggested(false);
  };

  const openCertRetryDateModal = async (row) => {
    if (!row?.client_id) return;
    setCertRetryDateModal(row);
    setCertRetryModalSuggested(null);
    setCertRetryModalSuggestedDb(null);
    setCertRetryModalSuggestedDmvic(null);
    setCertRetryModalOtherEnd(null);
    setCertRetryModalStart('');
    setCertRetryModalLoadingSuggested(true);
    try {
      const token = localStorage.getItem('employeeToken');
      const res = await fetch(
        `${getApiUrl('/api/employee/suggested-certificate-start-date')}?clientId=${encodeURIComponent(row.client_id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setCertRetryModalSuggestedDb(data.suggestedStartDateFromDb || null);
        setCertRetryModalSuggestedDmvic(data.suggestedStartDateFromDmvic || null);
        setCertRetryModalOtherEnd(data.latestOtherPolicyEndDate || null);
        if (data.suggestedStartDate) {
          setCertRetryModalSuggested(data.suggestedStartDate);
          setCertRetryModalStart(data.suggestedStartDate);
        }
      }
    } catch (_) {
      /* keep today default */
    } finally {
      setCertRetryModalLoadingSuggested(false);
    }
  };

  const submitCertRetryWithDate = () => {
    const reissueDate = String(certRetryModalSuggested || certRetryModalStart || '').trim().slice(0, 10);
    if (!certRetryDateModal?.client_id || !reissueDate) return;
    retryCertificateForClient(
      certRetryDateModal.client_id,
      { startDate: reissueDate },
      closeCertRetryDateModal
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPolicyDateOnly = (dateString) => {
    if (!dateString) return '—';
    const raw = String(dateString).trim();
    const parsed = raw.includes('/')
      ? (() => {
          const [day, month, year] = raw.split('/');
          return new Date(Number(year), Number(month) - 1, Number(day));
        })()
      : new Date(/^\d{4}-\d{2}-\d{2}/.test(raw) ? `${raw.slice(0, 10)}T12:00:00` : raw);
    if (isNaN(parsed.getTime())) return raw;
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { class: 'status-completed', text: 'Active' },
      completed: { class: 'status-completed', text: 'Completed' },
      success: { class: 'status-completed', text: 'Completed' },
      paid: { class: 'status-completed', text: 'Completed' },
      pending: { class: 'status-pending', text: 'Pending' },
      failed: { class: 'status-failed', text: 'Failed' }
    };
    const statusInfo = statusMap[status?.toLowerCase()] || { class: 'status-unknown', text: status || 'Unknown' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  // Handle view changes from sidebar
  const handleViewChange = (view) => {
    setActiveView(view);
    if (view === 'dashboard') {
      setShowCoverPurchase(false);
      setShowCertificateRecovery(false);
    } else if (view === 'purchase-cover') {
      setShowCertificateRecovery(false);
      setShowCoverPurchase(true);
      setPurchaseEntryStep('product-selection');
    } else if (view === 'extend-policy') {
      setShowCertificateRecovery(false);
      setShowCoverPurchase(true);
      setPurchaseEntryStep('extension-finder');
    } else if (view === 'certificate-recovery') {
      setShowCoverPurchase(false);
      setShowCertificateRecovery(true);
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.sidebar') && !event.target.closest('.mobile-menu-btn')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Update active view based on current state
  useEffect(() => {
    if (showCoverPurchase) setActiveView(purchaseEntryStep === 'extension-finder' ? 'extend-policy' : 'purchase-cover');
    else if (showCertificateRecovery) setActiveView('certificate-recovery');
    else setActiveView('dashboard');
  }, [showCoverPurchase, showCertificateRecovery, purchaseEntryStep]);

  return (
    <div className="agent-dashboard">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        />
      )}

      {/* Sidebar Navigation */}
      <EmployeeSidebar
        activeView={activeView}
        onViewChange={handleViewChange}
        user={user}
        onLogout={onLogout}
        onBackToMain={onBackToMain}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
      />

      {showCoverPurchase ? (
        <div className="agent-dashboard-main">
          <CoverPurchase
            agentId={user?.agentId || user?.id}
            agency={user?.tqCode != null || user?.kraPin ? { shortDescription: (user?.tqCode) || '', taxPin: user?.kraPin || '' } : undefined}
            agentUserId={user?.agentId ? user?.id : undefined}
            initialEntryStep={purchaseEntryStep}
            onClose={() => {
              setShowCoverPurchase(false);
              setPurchaseEntryStep('product-selection');
              // Refresh covers after purchase
              fetchCovers(1);
            }}
            onPurchaseComplete={(result) => {
              // Refresh covers after successful purchase
              fetchCovers(1);
            }}
          />
        </div>
      ) : showCertificateRecovery ? (
        <div className="agent-dashboard-main">
          <header className="dashboard-header">
            <div className="header-content">
              <div className="header-left">
                <button
                  className="mobile-menu-btn"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label="Toggle menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  <span className="menu-icon">☰</span>
                </button>
                <div className="header-title-section">
                  <div className="agency-name-display">
                    <span className="agency-icon">🏢</span>
                    <span className="agency-text">{user?.agencyName || 'Insurance Agency'}</span>
                  </div>
                  <h1 className="dashboard-title">Check Certificate Status</h1>
                  <p className="dashboard-subtitle">
                    Search your covers by vehicle registration, policy number, client ID, insured name, payment reference, or M-Pesa code to check and fix certificate status
                  </p>
                  <p className="ira-number">Employee: {user?.fullName || user?.username || 'Employee'}</p>
                </div>
              </div>
            </div>
          </header>
          <main className="agent-dashboard-content">
            <div className="business-section" style={{ padding: '0 0.75rem' }}>
              <h2>Check Certificate Status</h2>
              <p style={{ color: '#555', marginBottom: '1rem', maxWidth: '48rem', lineHeight: 1.5 }}>
                Covers assigned to you where a certificate has not been issued yet. Recover queues DMVIC issuance.
                If another cover on the same plate is still active, use <strong>With date</strong>—the suggested start is the day after that cover ends (merged with DMVIC when available).
                If issuance fails because of contact details, use Fix & Retry.
                When the error is only that details do not match NTSA, use <strong>Correct plate</strong> with the full registration from NTSA.
              </p>
              {certRetryMessage && (
                <div
                  className={
                    certRetryMessage.startsWith('Certificate queued') || certRetryMessage.includes('successfully')
                      ? 'success-alert'
                      : 'error-alert'
                  }
                  style={{ marginBottom: '12px' }}
                >
                  {certRetryMessage}
                </div>
              )}
              {certRecoveryError && (
                <div className="error-alert" style={{ marginBottom: '12px' }}>
                  ⚠️ {certRecoveryError}
                </div>
              )}
              <div className="filters-section" style={{ marginBottom: '1rem' }}>
                <div className="filter-group">
                  <input
                    type="text"
                    placeholder="Search by vehicle, policy, client ID, insured name, reference, or M-Pesa code…"
                    value={certRecoverySearch}
                    onChange={(e) => setCertRecoverySearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchCertificateRecovery(1)}
                    className="filter-input"
                  />
                  <select
                    className="filter-select"
                    value={certRecoveryStatusScope}
                    onChange={(e) => setCertRecoveryStatusScope(e.target.value)}
                    style={{ marginLeft: '8px' }}
                  >
                    <option value="pending">Pending only</option>
                    <option value="all">All statuses</option>
                  </select>
                  <p style={{ margin: '8px 0 0 0', color: '#555', fontSize: '0.9rem' }}>
                    Tip: choose a status in the dropdown first, then click Search.
                  </p>
                  <button className="btn-filter" type="button" onClick={() => fetchCertificateRecovery(1)} style={{ marginTop: '8px' }}>
                    Search
                  </button>
                </div>
              </div>
              {certRecoveryLoading ? (
                <div className="loading-state">
                  <div className="spinner-large"></div>
                  <p>Loading…</p>
                </div>
              ) : certRecoveryItems.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">✅</div>
                  <h3>{certRecoveryStatusScope === 'all' ? 'No covers found' : 'No pending certificate checks'}</h3>
                  <p>
                    {certRecoveryStatusScope === 'all'
                      ? 'No paid covers match your current search.'
                      : 'All your paid covers have certificates, or nothing matches your search.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="table-container" style={{ overflowX: 'auto' }}>
                    <table className="business-table">
                      <thead>
                        <tr>
                          <th>Payment completed</th>
                          <th>Insured</th>
                          <th>Vehicle registration</th>
                          <th>Policy</th>
                          <th>Cover</th>
                          <th>Payment ref</th>
                          <th>Status / note</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {certRecoveryItems.map((row) => {
                          const isIssued = row.certificate_status === 'issued' || !!String(row.dmvic_certificate_number || '').trim();
                          return (
                          <tr key={row.client_id}>
                            <td>{formatDate(row.payment_completed_at)}</td>
                            <td>{row.insured_name || row.client_id || 'N/A'}</td>
                            <td>{row.registration_number || '—'}</td>
                            <td>{row.policy_number || 'N/A'}</td>
                            <td>{[row.cover_type, row.vehicle_class].filter(Boolean).join(' · ') || 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                <span style={{ wordBreak: 'break-all' }}>{row.payment_reference || '—'}</span>
                                {row.payment_confirmation_code && (
                                  <span style={{ color: '#0f5132', fontWeight: 700 }}>
                                    M-Pesa: {row.payment_confirmation_code}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span title={row.certificate_last_error || ''} style={{ fontSize: '0.9rem' }}>
                                {isIssued ? 'Issued' : (row.certificate_status === 'failed' ? 'Failed' : 'Not issued')}
                                {row.certificate_last_error
                                  ? ` — ${String(row.certificate_last_error).slice(0, 100)}${String(row.certificate_last_error).length > 100 ? '…' : ''}`
                                  : ''}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn-retry-cert"
                                disabled={certRetryingClientId === row.client_id || isIssued}
                                onClick={() => retryCertificateForClient(row.client_id)}
                              >
                                {certRetryingClientId === row.client_id ? 'Queuing…' : (isIssued ? 'Issued' : 'Recover')}
                              </button>
                              {!isIssued && (
                                <button
                                  type="button"
                                  className="btn-retry-date"
                                  style={{ marginLeft: '8px' }}
                                  disabled={certRetryingClientId === row.client_id}
                                  onClick={() => openCertRetryDateModal(row)}
                                  title="Retry with a new start date (e.g. day after another cover on this plate ends)"
                                >
                                  With date
                                </button>
                              )}
                              {row.certificate_status === 'failed' && !isIssued && (
                                <button
                                  type="button"
                                  className="btn-filter"
                                  style={{ marginLeft: '8px' }}
                                  disabled={certRetryingClientId === row.client_id}
                                  onClick={() => openFixAndRetry(row)}
                                >
                                  Fix & Retry
                                </button>
                              )}
                              {row.ntsa_registration_mismatch_only && !isIssued && (
                                <button
                                  type="button"
                                  className="btn-correct-plate"
                                  style={{ marginLeft: '8px' }}
                                  disabled={certRetryingClientId === row.client_id}
                                  onClick={() => correctPlateNtsaMismatchAndRecover(row)}
                                  title="Wrong or incomplete plate: enter full NTSA registration, sync vehicle, then queue certificate"
                                >
                                  Correct plate
                                </button>
                              )}
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                  {certRecoveryPagination.totalPages > 1 && (
                    <div className="pagination">
                      <button
                        type="button"
                        className="btn-pagination"
                        onClick={() => fetchCertificateRecovery(certRecoveryPagination.page - 1)}
                        disabled={certRecoveryPagination.page === 1}
                      >
                        ← Previous
                      </button>
                      <span className="pagination-info">
                        Page {certRecoveryPagination.page} of {certRecoveryPagination.totalPages} ({certRecoveryPagination.total}{' '}
                        total)
                      </span>
                      <button
                        type="button"
                        className="btn-pagination"
                        onClick={() => fetchCertificateRecovery(certRecoveryPagination.page + 1)}
                        disabled={certRecoveryPagination.page >= certRecoveryPagination.totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
          {certRetryDateModal && (
            <div className="paid-no-cert-modal-overlay" onClick={closeCertRetryDateModal} role="dialog" aria-modal="true" aria-labelledby="emp-cert-retry-date-title">
              <div className="paid-no-cert-modal" onClick={(e) => e.stopPropagation()}>
                <div className="paid-no-cert-modal-header">
                  <h3 id="emp-cert-retry-date-title">Re-issue after active cover ends</h3>
                  <button type="button" className="paid-no-cert-modal-close" onClick={closeCertRetryDateModal} aria-label="Close">×</button>
                </div>
                <p className="paid-no-cert-modal-meta">
                  {certRetryDateModal.insured_name || certRetryDateModal.client_id} · {certRetryDateModal.registration_number || '—'}
                </p>
                {certRetryModalLoadingSuggested ? (
                  <p className="paid-no-cert-modal-loading">Checking DMVIC for the next available start date…</p>
                ) : certRetryModalSuggested ? (
                  <div className="paid-no-cert-modal-suggested">
                    {certRetryModalOtherEnd && (
                      <p style={{ marginTop: 0 }}>
                        Existing active cover ends on <strong>{formatPolicyDateOnly(certRetryModalOtherEnd)}</strong>.
                      </p>
                    )}
                    <p>
                      Next available start date: <strong>{formatPolicyDateOnly(certRetryModalSuggested)}</strong>
                    </p>
                    <p className="paid-no-cert-modal-suggested-note" style={{ marginTop: '0.35rem' }}>
                      Day after the previous policy expires. This date will be saved and sent to DMVIC.
                    </p>
                  </div>
                ) : (
                  <div className="paid-no-cert-modal-suggested" style={{ color: '#721c24' }}>
                    <p style={{ marginTop: 0, fontWeight: 600 }}>Cannot auto-reissue yet.</p>
                    <p className="paid-no-cert-modal-suggested-note">
                      The next available start date is more than 30 days away, or DMVIC did not return one. Try again when the current policy is within 30 days of expiry, or contact D-MVIC support.
                    </p>
                  </div>
                )}
                <div className="paid-no-cert-modal-actions">
                  <button
                    type="button"
                    className="btn-retry-date-submit"
                    onClick={submitCertRetryWithDate}
                    disabled={!certRetryModalSuggested || certRetryModalLoadingSuggested || certRetryingClientId === certRetryDateModal.client_id}
                  >
                    {certRetryModalSuggested
                      ? `Re-issue certificate from ${formatPolicyDateOnly(certRetryModalSuggested)}`
                      : 'Re-issue certificate'}
                  </button>
                  <button type="button" className="btn-retry-date-cancel" onClick={closeCertRetryDateModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="agent-dashboard-main">
          <header className="dashboard-header">
            <div className="header-content">
              <div className="header-left">
                {/* Mobile Menu Button */}
                <button 
                  className="mobile-menu-btn"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label="Toggle menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  <span className="menu-icon">☰</span>
                </button>
                
                <div className="header-title-section">
                  {/* Agency Name - Always Visible */}
                  <div className="agency-name-display">
                    <span className="agency-icon">🏢</span>
                    <span className="agency-text">{user?.agencyName || 'Insurance Agency'}</span>
                  </div>
                  
                  <h1 className="dashboard-title">Employee Dashboard</h1>
                  <p className="dashboard-subtitle">Manage your insurance covers and policies</p>
                  <p className="ira-number">Employee: {user?.fullName || user?.username || 'Employee'}</p>
                </div>
              </div>
            </div>
          </header>

      <main className="agent-dashboard-content">
        <div className="statistics-grid">
          <div className="stat-card">
            <div className="stat-icon">📋</div>
            <div className="stat-content">
              <h3>Total Covers</h3>
              <p className="stat-value">{pagination.total}</p>
            </div>
          </div>
        </div>

        <div className="business-section">
          <h2>My Covers</h2>
          {error && (
            <div className="error-alert">
              ⚠️ {error}
            </div>
          )}
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner-large"></div>
              <p>Loading covers...</p>
            </div>
          ) : covers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>No covers found</h3>
              <p>Your covers will appear here once you start processing policies.</p>
            </div>
          ) : (
            <>
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="business-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Certificate #</th>
                      <th>Policy #</th>
                      <th>Insured Person</th>
                      <th>Vehicle #</th>
                      <th>Cover Type</th>
                      <th>Premium</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {covers.map((cover) => (
                      <tr key={cover.id}>
                        <td>{formatDate(cover.issuance_on)}</td>
                        <td>{cover.certificate_number || 'N/A'}</td>
                        <td>{cover.policy_number || 'N/A'}</td>
                        <td>{cover.insured_person || 'N/A'}</td>
                        <td>{cover.vehicle_number || 'N/A'}</td>
                        <td>{cover.certificate_type || 'N/A'}</td>
                        <td className="amount-cell">{formatCurrency(cover.premium)}</td>
                        <td>{getStatusBadge(cover.certificate_status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn-pagination"
                    onClick={() => fetchCovers(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    ← Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                  </span>
                  <button
                    className="btn-pagination"
                    onClick={() => fetchCovers(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;
